import { useEnvironmentContext } from '@openeducation/oe-it-providers';
import type { AxiosError } from 'axios';
import { useMutation, useQuery } from 'react-query';

import {
  ActiveClass,
  ActiveClassCurrentClass,
} from '../interfaces/active-class';
import { QueryKeys } from '../interfaces/query-keys';
import { ActiveClasses } from '../services/active-classes.service';

const TWENTY_SECONDS_DELAY_MS = 20000;

interface UseActiveClassesProps {
  teacherId: number;
  isEnabled?: boolean;
}
export function useActiveClasses({
  teacherId,
  isEnabled = true,
}: UseActiveClassesProps) {
  const { environment } = useEnvironmentContext();

  const {
    data: activeClasses,
    isLoading: isLoadingActiveClasses,
    error: activeClassesError,
  } = useQuery<ActiveClassCurrentClass, AxiosError>(
    [QueryKeys.ActiveClasses, teacherId],
    () => ActiveClasses.getActiveClasses(environment, teacherId),
    {
      enabled: isEnabled && !!teacherId,
      refetchInterval: TWENTY_SECONDS_DELAY_MS,
    }
  );

  const totalStudent = activeClasses?.currentClasses?.reduce(
    (total: number, item: ActiveClass) => total + item?.studentNames?.length,
    0
  );

  const { mutateAsync: reassignTeacher } = useMutation(
    ({
      originalTeacherId,
      classroomId,
    }: {
      originalTeacherId: number;
      classroomId: number;
    }) => {
      return ActiveClasses.reassignTeacher(
        environment,
        originalTeacherId,
        classroomId,
        teacherId
      );
    }
  );

  const currentClasses = activeClasses?.currentClasses?.map((currentClass) => {
    const teacherIsnotPresent = activeClasses.tnp.some(
      (item) => item.id === currentClass.id && !item.teacherPresent
    );

    return {
      ...currentClass,
      teacherPresent: !teacherIsnotPresent && currentClass.teacherPresent,
    };
  });

  return {
    isLoadingActiveClasses,
    activeClasses: currentClasses,
    activeClassesError,
    totalStudent: totalStudent || 0,
    reassignTeacher,
  };
}
